import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Checkbox, FormControlLabel, InputAdornment, MenuItem, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';
import { setLoading } from 'Actions/loading';
import { setFlow } from 'Actions/flow';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import http from 'Utils/http';
import { useCreateUrl } from 'Hooks/useCreateUrl';
import { process } from 'Utils/errorHandler';

const ROUTE_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const HMAC_ALGORITHMS = ['sha256', 'sha1', 'sha512'];
const HMAC_ENCODINGS = ['hex', 'base64'];

const toFormValues = (flow) => ({
    route_method: flow?.route_method || 'POST',
    route_path: flow?.route_path || '',
    hmac_enabled: !!flow?.hmac_settings,
    hmac_header: flow?.hmac_settings?.header || '',
    hmac_algorithm: flow?.hmac_settings?.algorithm || 'sha256',
    hmac_encoding: flow?.hmac_settings?.encoding || 'hex',
    hmac_prefix: flow?.hmac_settings?.prefix || '',
    hmac_secret: '',
    no_auth: !!flow?.no_auth,
});

const SelectField = ({ name, label, options, control }) => (
    <Controller
        name={name}
        control={control}
        render={({ field }) => (
            <TextField {...field} select id={name} label={label} sx={{ minWidth: '120px' }} InputLabelProps={{ shrink: true }}>
                {options.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
            </TextField>
        )}
    />
);

const CheckboxField = ({ name, label, control, onCheck }) => (
    <FormControlLabel
        control={
            <Controller
                name={name}
                control={control}
                render={({ field }) => (
                    <Checkbox
                        {...field}
                        checked={!!field.value}
                        onChange={(e) => {
                            field.onChange(e.target.checked);
                            if (e.target.checked) {
                                onCheck?.();
                            }
                        }}
                    />
                )}
            />
        }
        label={label}
        sx={{ marginTop: '10px', '& .MuiTypography-root': { color: '#3B465C' } }}
    />
);

export const FlowEndpointForm = ({ copyButton }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { createUrl } = useCreateUrl();
    const flow = useSelector((state) => state.flow.flow);

    const {
        register,
        control,
        handleSubmit,
        reset,
        setError,
        setValue,
        watch,
        formState: { errors, isDirty },
    } = useForm({ defaultValues: toFormValues(flow) });

    const hmacEnabled = watch('hmac_enabled');

    useEffect(() => {
        reset(toFormValues(flow));
    }, [flow, reset]);

    const onSubmit = (data) => {
        const params = {
            route_method: data.route_path.trim() ? data.route_method : '',
            route_path: data.route_path.trim(),
            hmac_settings: data.hmac_enabled ? {
                header: data.hmac_header.trim(),
                algorithm: data.hmac_algorithm,
                encoding: data.hmac_encoding,
                prefix: data.hmac_prefix,
            } : null,
            ...(data.hmac_enabled && data.hmac_secret && { hmac_secret: data.hmac_secret }),
            no_auth: data.no_auth,
        };
        dispatch(setLoading(true));
        http.post(createUrl(`/flow/${flow.id}/update`), params).then((res) => {
            dispatch(setFlow(res.data.flow));
            enqueueSnackbar('Flow endpoint has been updated', {
                action: CustomSnackbar,
                variant: 'success'
            });
        }).catch((err) => {
            const fieldErrors = err.response?.status === 422 ? err.response?.data?.errors : null;
            if (fieldErrors?.route_path?.[0]) {
                setError('route_path', { message: fieldErrors.route_path[0] });
                return;
            }
            if (fieldErrors?.hmac_settings?.[0]) {
                setError('hmac_header', { message: fieldErrors.hmac_settings[0] });
                return;
            }
            process(err, enqueueSnackbar);
        }).finally(() => {
            dispatch(setLoading(false));
        });
    };

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ marginBottom: '25px' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <SelectField name="route_method" label="Method" options={ROUTE_METHODS} control={control} />
                <TextField
                    error={errors?.route_path ? true : false}
                    helperText={errors?.route_path?.message || ''}
                    fullWidth
                    id="route_path"
                    label="Path"
                    placeholder={flow?.url?.slice(flow?.url_host?.length ?? 0)}
                    inputProps={{ maxLength: 255 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">{flow?.url_host}</InputAdornment> }}
                    InputLabelProps={{ shrink: true }}
                    {...register('route_path', { maxLength: 255 })}
                />
                {copyButton}
            </Box>
            <CheckboxField name="no_auth" label="No authorization" control={control} onCheck={() => setValue('hmac_enabled', false, { shouldDirty: true })} />
            <CheckboxField name="hmac_enabled" label="HMAC authorization" control={control} onCheck={() => setValue('no_auth', false, { shouldDirty: true })} />
            {hmacEnabled && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                    <TextField
                        error={errors?.hmac_header ? true : false}
                        helperText={errors?.hmac_header?.message || ''}
                        id="hmac_header"
                        label="HMAC signature header"
                        placeholder="X-Signature"
                        sx={{ flex: '1 1 200px' }}
                        inputProps={{ maxLength: 64 }}
                        InputLabelProps={{ shrink: true }}
                        {...register('hmac_header', { maxLength: 64 })}
                    />
                    <SelectField name="hmac_algorithm" label="Algorithm" options={HMAC_ALGORITHMS} control={control} />
                    <SelectField name="hmac_encoding" label="Encoding" options={HMAC_ENCODINGS} control={control} />
                    <TextField
                        id="hmac_prefix"
                        label="Prefix"
                        placeholder="sha256="
                        sx={{ width: '120px' }}
                        inputProps={{ maxLength: 32 }}
                        InputLabelProps={{ shrink: true }}
                        {...register('hmac_prefix', { maxLength: 32 })}
                    />
                    <TextField
                        id="hmac_secret"
                        label="Secret"
                        type="password"
                        autoComplete="new-password"
                        placeholder={flow?.hmac_settings ? 'Unchanged' : ''}
                        sx={{ flex: '1 1 200px' }}
                        InputLabelProps={{ shrink: true }}
                        {...register('hmac_secret')}
                    />
                </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px' }}>
                <Button type="submit" variant="contained" disabled={!isDirty}>
                    Save
                </Button>
            </Box>
        </Box>
    );
};
